import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api.services';
import {
	UserNotificationPreferencesResponse,
	UserDeviceGroup,
} from 'src/app/models/model';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/list';

@Component({
	selector: 'app-notification-preferences',
	templateUrl: './notification-preferences.component.html',
	styleUrls: [
		'../../common/main.scss',
		'./notification-preferences.component.scss',
	],
	imports: [MatCheckbox, MatButton, MatDivider],
})
export class NotificationPreferencesComponent implements OnInit {
	isEditingNotifications = false;
	isSmsNotificationEnabled = false;
	userNotificationPreferences: UserDeviceGroup[] = [];
	editUserNotificationPreferences: UserDeviceGroup[] = [];

	constructor(
		private service: ApiService,
		private banner: BannerMessageService
	) {}

	async ngOnInit(): Promise<void> {
		// checks that the active company has the sms Notifications feature enabled
		this.service
			.getCompanySettingByName('smsNotifications')
			.then((companySetting) => {
				if (companySetting?.value.toLowerCase().startsWith('true')) {
					this.isSmsNotificationEnabled = true;
				} else {
					this.isSmsNotificationEnabled = false;
				}
			});

		await this.getUserNotificationPreferences();
	}

	async getUserNotificationPreferences(): Promise<void> {
		const userNotificationPreferencesResponse =
			await this.service.getUserNotificationPreferences();
		this.userNotificationPreferences =
			userNotificationPreferencesResponse.userNotificationPreferences;
		this.cloneUserNotificationPreferences();
	}

	/** Perfom a deep copy of the userNotificationPreferences so we can keep the original object
	 * to use to find what the user changed.
	 */
	cloneUserNotificationPreferences() {
		this.editUserNotificationPreferences = JSON.parse(
			JSON.stringify(this.userNotificationPreferences)
		);
	}

	async saveNotificationPreferences(): Promise<void> {
		try {
			const changedUserNotificationPreferences =
				this.getChangedNotificationPreferencesResponse();

			if (
				changedUserNotificationPreferences?.userNotificationPreferences
					?.length &&
				changedUserNotificationPreferences?.userNotificationPreferences
					?.length > 0
			) {
				const userNotificationPreferencesResponse =
					await this.service.updateUserNotificationPreferences(
						changedUserNotificationPreferences
					);
				this.userNotificationPreferences =
					userNotificationPreferencesResponse.userNotificationPreferences;
				this.cloneUserNotificationPreferences();

				this.banner.sendMessage(
					$localize`:@@notificationPreferences-successful:Notification preferences updated successfully.`
				);
			}

			this.isEditingNotifications = false;
		} catch (error) {
			this.banner.sendMessage(
				$localize`:@@notificationPreferences-errorMessage:Error updating notification preferences.`,
				BannerMessageType.error
			);
		}
	}

	/** Get just the changes to return to the server to save. */
	getChangedNotificationPreferencesResponse(): UserNotificationPreferencesResponse {
		// just get the changes
		let changedPreferences = [];
		for (const editUserNotificationPreference of this
			.editUserNotificationPreferences) {
			const matchingUserNotificationPreference =
				this.userNotificationPreferences.find(
					(userNotificationPreference) =>
						userNotificationPreference.deviceGroupId ===
							editUserNotificationPreference.deviceGroupId &&
						userNotificationPreference.userId ===
							editUserNotificationPreference.userId
				);

			// just get the changes for this preference if any
			if (
				editUserNotificationPreference.userDeviceGroupNotificationPreferences
			) {
				let changedUserDeviceGroupNotificationPreferences = [];
				for (const editUserDeviceGroupNotificationPreference of editUserNotificationPreference.userDeviceGroupNotificationPreferences) {
					// find the matching preference from the unmodified server data so we can see if it's been changed
					const userDeviceGroupNotificationPreference =
						matchingUserNotificationPreference?.userDeviceGroupNotificationPreferences?.find(
							(userDeviceGroupNotificationPreference) =>
								editUserDeviceGroupNotificationPreference.userNotificationEventTypeId ===
									userDeviceGroupNotificationPreference.userNotificationEventTypeId &&
								editUserDeviceGroupNotificationPreference.userDeviceGroupId ===
									userDeviceGroupNotificationPreference.userDeviceGroupId
						);

					if (
						editUserDeviceGroupNotificationPreference.receiveEmail !==
							userDeviceGroupNotificationPreference?.receiveEmail ||
						editUserDeviceGroupNotificationPreference.receiveSms !==
							userDeviceGroupNotificationPreference?.receiveSms
					) {
						// there were some changes in this preference so include it in what we will send to the server
						changedUserDeviceGroupNotificationPreferences.push(
							editUserDeviceGroupNotificationPreference
						);
					}
				}

				// if we found any changes in the preferences we have to include it in the data to be returned to the server
				if (changedUserDeviceGroupNotificationPreferences.length > 0) {
					let changedUserNotificationPreference = {
						id: editUserNotificationPreference.id,
						userId: editUserNotificationPreference.userId,
						deviceGroupId: editUserNotificationPreference.deviceGroupId,
						deviceGroupName: editUserNotificationPreference.deviceGroupName,
						userDeviceGroupNotificationPreferences:
							changedUserDeviceGroupNotificationPreferences,
					};
					changedPreferences.push(changedUserNotificationPreference);
				}
			}
		}
		return { userNotificationPreferences: changedPreferences };
	}

	cancelEditing(): void {
		this.isEditingNotifications = false;
		this.cloneUserNotificationPreferences();
	}

	isSelectedAllEmailChecked(): boolean {
		let allChecked = true;
		for (const editUserNotificationPreference of this
			.editUserNotificationPreferences) {
			if (
				editUserNotificationPreference.userDeviceGroupNotificationPreferences
			) {
				for (const userDeviceGroupNotificationPreference of editUserNotificationPreference.userDeviceGroupNotificationPreferences) {
					allChecked =
						allChecked && userDeviceGroupNotificationPreference.receiveEmail;
				}
			}
		}

		return allChecked;
	}

	isSelectAllEmailIntermediate(): boolean {
		return this.editUserNotificationPreferences.length < 1;
	}

	onSelectAllEmailToggle(checked: boolean): void {
		for (const editUserNotificationPreference of this
			.editUserNotificationPreferences) {
			if (
				editUserNotificationPreference.userDeviceGroupNotificationPreferences
			) {
				for (const userDeviceGroupNotificationPreference of editUserNotificationPreference.userDeviceGroupNotificationPreferences) {
					userDeviceGroupNotificationPreference.receiveEmail = checked;
				}
			}
		}
	}

	isSelectedAllSmsChecked(): boolean {
		let allChecked = true;
		for (const editUserNotificationPreference of this
			.editUserNotificationPreferences) {
			if (
				editUserNotificationPreference.userDeviceGroupNotificationPreferences
			) {
				for (const userDeviceGroupNotificationPreference of editUserNotificationPreference.userDeviceGroupNotificationPreferences) {
					allChecked =
						allChecked && userDeviceGroupNotificationPreference.receiveSms;
				}
			}
		}

		return allChecked;
	}

	isSelectAllSmsIntermediate(): boolean {
		return this.editUserNotificationPreferences.length < 1;
	}

	onSelectAllSmsToggle(checked: boolean): void {
		for (const editUserNotificationPreference of this
			.editUserNotificationPreferences) {
			if (
				editUserNotificationPreference.userDeviceGroupNotificationPreferences
			) {
				for (const userDeviceGroupNotificationPreference of editUserNotificationPreference.userDeviceGroupNotificationPreferences) {
					userDeviceGroupNotificationPreference.receiveSms = checked;
				}
			}
		}
	}
}
